import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, ChevronsDown, ChevronsUp, Trash2 } from 'lucide-react'

import { api } from '@/api/client'
import { inputCls } from '@/components/Field'
import { useToast } from '@/stores/toast'
import { confirm } from '@/stores/confirm'
import { cn } from '@/lib/utils'
import { useDebouncedValue, useRenderLimit } from '@/lib/useListPerf'
import { ShowMoreRow } from '@/components/ShowMoreRow'

interface OrderedName { purchase_item: string; position: number; items: number }
interface NameOrderResponse { ordered: OrderedName[] }

const QK = ['purchase-name-order']

/** 구매장 > 정렬 — 구매장용 품명 묶음의 표시 순서(구매장·라벨 보기 공통). */
export function PurchaseNameOrderTab() {
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const [search, setSearch] = useState('')
  const [posInput, setPosInput] = useState<Record<string, string>>({})

  const { data, isFetching } = useQuery({
    queryKey: QK,
    queryFn: () => api<NameOrderResponse>('/api/purchase/name-order'),
  })
  const ordered = data?.ordered ?? []
  const total = ordered.length

  const move = useMutation({
    mutationFn: (v: { purchase_item: string; position: number | null }) =>
      api('/api/purchase/name-order', { method: 'POST', body: v }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: QK })
      qc.invalidateQueries({ queryKey: ['purchase-register-all'] })
      setPosInput((s) => ({ ...s, [v.purchase_item]: '' }))
    },
    onError: (e) => push(`순서 변경 실패: ${e.message}`, 'error', 5000),
  })

  const q = useDebouncedValue(search.trim().toLowerCase(), 200)
  const shownOrdered = useMemo(
    () => (q ? ordered.filter((r) => r.purchase_item.toLowerCase().includes(q)) : ordered), [ordered, q])
  const list = useRenderLimit(shownOrdered, q)

  function go(name: string, position: number | null) {
    if (!move.isPending) move.mutate({ purchase_item: name, position })
  }
  function goTyped(name: string) {
    const n = parseInt(posInput[name] ?? '', 10)
    if (Number.isFinite(n) && n > 0) go(name, n)
  }

  const btn = 'p-1 rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-30'
  const posBox = (name: string) => (
    <input value={posInput[name] ?? ''} inputMode="numeric" placeholder="위치"
           onChange={(e) => setPosInput((s) => ({ ...s, [name]: e.target.value.replace(/\D/g, '') }))}
           onKeyDown={(e) => { if (e.key === 'Enter') goTyped(name) }}
           className={inputCls + ' w-16 !py-1 text-xs tabular-nums'}/>
  )

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap shadow-sm mb-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="구매장용 품명 검색" className={inputCls + ' flex-1 min-w-[220px]'}/>
        <span className="text-xs text-slate-400 tabular-nums">
          품명 {total}개{isFetching ? ' · 불러오는 중…' : ''}
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-3 px-1">
        구매장·라벨 보기에서 품명 묶음이 이 순서로 나옵니다. 위치 칸에 번호를 넣고 Enter 하면 그 자리로 이동합니다.
        품목 매핑에서 새 품명을 등록하면 맨 아래에 자동으로 추가됩니다.
      </p>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500">
            <tr>
              <th className="px-3 py-2 text-right font-medium w-16">순서</th>
              <th className="px-3 py-2 text-left font-medium">구매장용 품명</th>
              <th className="px-3 py-2 text-right font-medium w-20">품목 수</th>
              <th className="px-3 py-2 text-right font-medium w-64">이동</th>
            </tr>
          </thead>
          <tbody>
            {list.shown.map((r) => (
              <tr key={r.purchase_item} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-1 text-right tabular-nums text-slate-500">{r.position}</td>
                <td className={cn('px-3 py-1 font-medium', r.items ? 'text-slate-900' : 'text-slate-400')}>
                  {r.purchase_item}
                  {!r.items && <span className="ml-2 text-xs font-normal">(사용 안 함)</span>}
                </td>
                <td className="px-3 py-1 text-right tabular-nums text-slate-500">{r.items}</td>
                <td className="px-3 py-1">
                  <div className="flex items-center justify-end gap-0.5">
                    <button title="맨 위로" className={btn} disabled={r.position === 1 || move.isPending}
                            onClick={() => go(r.purchase_item, 1)}><ChevronsUp className="w-4 h-4"/></button>
                    <button title="위로" className={btn} disabled={r.position === 1 || move.isPending}
                            onClick={() => go(r.purchase_item, r.position - 1)}><ArrowUp className="w-4 h-4"/></button>
                    <button title="아래로" className={btn} disabled={r.position === total || move.isPending}
                            onClick={() => go(r.purchase_item, r.position + 1)}><ArrowDown className="w-4 h-4"/></button>
                    <button title="맨 아래로" className={btn} disabled={r.position === total || move.isPending}
                            onClick={() => go(r.purchase_item, total)}><ChevronsDown className="w-4 h-4"/></button>
                    <span className="ml-1">{posBox(r.purchase_item)}</span>
                    {!r.items ? (
                      <button title="삭제 (사용 안 하는 품명)" className={btn + ' hover:!text-rose-600'} disabled={move.isPending}
                              onClick={() => { void confirm(`'${r.purchase_item}' 품명을 정렬 목록에서 삭제할까요?`, { danger: true })
                                .then((ok) => ok && go(r.purchase_item, null)) }}>
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    ) : <span className="w-6"/>}
                  </div>
                </td>
              </tr>
            ))}
            <ShowMoreRow hidden={list.hidden} colSpan={4} onMore={list.more} onAll={list.all}/>
            {!shownOrdered.length && !isFetching && (
              <tr><td colSpan={4} className="text-center py-8 text-sm text-slate-400">
                {q ? '검색 결과가 없습니다.' : '품명이 없습니다. 품목 매핑을 등록하면 여기에 추가됩니다.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
